// ============================================================
// APP.JS — PART 1
// Imports + State + Hooks
// ============================================================

import React, {
  useEffect,
  useState,
} from "react";


// ============================================================
// AUTH
// ============================================================

import { useAuth } from "./hooks/useAuth";
import LanguageSelector from "./components/LanguageSelector";
import { useLanguage } from "./i18n/LanguageContext";

// ============================================================
// PRACTICE
// ============================================================

import {
  usePracticeQuestions,
} from "./hooks/usePracticeQuestions";

import {
  usePracticeSubjects,
} from "./hooks/usePracticeSubjects";

import {
  useEntitlements,
} from "./hooks/useEntitlements";

import PracticeHomePage from "./components/practice/PracticeHomePage";
import PracticeSubjectsPage from "./components/practice/PracticeSubjectsPage";
import PracticeTopicsPage from "./components/practice/PracticeTopicsPage";
import PracticeQuestionPage from "./components/practice/PracticeQuestionPage";
import PracticeRoundCompletePage from "./components/practice/PracticeRoundCompletePage";


// ============================================================
// GENERAL STYLES
// ============================================================

import {
  styles,
} from "./styles/styles";


// ============================================================
// MAIN / LEGACY COMPONENTS
// ============================================================

import HomePage from "./components/HomePage";
import AccountPage from "./components/AccountPage";
import ContactPage from "./components/ContactPage";
import TermsPage from "./components/TermsPage";
import PrivacyPage from "./components/PrivacyPage";
import AdminDashboard from "./components/AdminDashboard";
import ClassExamPage from "./components/ClassExamPage";


// ============================================================
// ADMIN / EXAM HOOKS
// ============================================================

import {
  useStudents,
} from "./hooks/useStudents";

import {
  useExamSessions,
} from "./hooks/useExamSessions";

import {
  useOpenExamSessions,
} from "./hooks/useOpenExamSessions";

import {
  useExamQuestions,
} from "./hooks/useExamQuestions";

import {
  useExamAttempt,
} from "./hooks/useExamAttempt";

import {
  useExamResults,
} from "./hooks/useExamResults";

import {
  useQuestionTeachingNotes,
} from "./hooks/useQuestionTeachingNotes";

import {
  useStudentQuestionNotes,
} from "./hooks/useStudentQuestionNotes";

import {
  useRetakePermissions,
} from "./hooks/useRetakePermissions";

import {
  useCourses,
} from "./hooks/useCourses";

import {
  useCohortEnrollments,
} from "./hooks/useCohortEnrollments";


// ============================================================
// WRONG QUESTIONS
// ============================================================

import {
  useWrongQuestionProgress,
} from "./hooks/useWrongQuestionProgress";

import WrongQuestionHomePage from "./components/wrongQuestions/WrongQuestionHomePage";

// ============================================================
// LEARNING
// ============================================================

import {
  useLearning,
} from "./hooks/useLearning";

import {
  useLearningExercises,
} from "./hooks/useLearningExercises";

import {
  useLearningProgress,
} from "./hooks/useLearningProgress";

import LearningCenterPage from "./components/learning/LearningCenterPage";
import LearningUnitPage from "./components/learning/LearningUnitPage";
import LearningTopicPage from "./components/learning/LearningTopicPage";


// ============================================================
// VOCABULARY
// ============================================================

import {
  useVocabulary,
} from "./hooks/useVocabulary";

import VocabularyHomePage from "./components/vocabulary/VocabularyHomePage";
import VocabularyTopicPage from "./components/vocabulary/VocabularyTopicPage";

import VocabularyFlashcards from "./components/vocabulary/practice/VocabularyFlashcards";
import VocabularyMatching from "./components/vocabulary/practice/VocabularyMatching";
import VocabularyQuiz from "./components/vocabulary/practice/VocabularyQuiz";


// ============================================================
// APP
// ============================================================

export default function App() {

  const { t, language } = useLanguage();

  // =========================================================
  // PAGE NAVIGATION
  // =========================================================

  const [
    page,
    setPage,
  ] = useState("home");

  const openBillingPortal = async () => {
    try {
      const response = await fetch(
        "/api/create-billing-portal-session",
        {
          method: "POST",
          headers: {
            Authorization:
              `Bearer ${session.access_token}`,
          },
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        alert(
          data.error ||
          "Unable to open billing portal."
        );
        return;
      }

      window.location.href =
        data.portalUrl;

    } catch (error) {
      console.error(
        "BILLING PORTAL ERROR:",
        error
      );

      alert(
        "Unable to open billing portal."
      );
    }
  };

  const startSubscriptionCheckout = async (plan = "monthly") => {
    try {
      if (!session?.access_token) {
        alert("Please log in first.");
        return;
      }

      const response = await fetch(
        "/api/create-checkout-session",
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Authorization:
              `Bearer ${session.access_token}`,
          },

          body: JSON.stringify({
            plan,
          }),
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        alert(
          data?.error ||
            "Unable to create checkout."
        );
        return;
      }

      if (!data?.checkoutUrl) {
        alert(
          "Stripe checkout URL was not returned."
        );
        return;
      }

      window.location.href =
        data.checkoutUrl;

    } catch (error) {
      console.error(
        "STRIPE CHECKOUT ERROR:",
        error
      );

      alert(
        "Unable to start subscription checkout."
      );
    }
  };

  // =========================================================
  // SHARED QUESTION NAVIGATION STATE
  //
  // Some of this is still shared with the legacy QuizPage.
  // We will split it further after Federal Exam migration.
  // =========================================================

  const [
    currentIndex,
    setCurrentIndex,
  ] = useState(0);

  // =========================================================
  // PRACTICE NAVIGATION STATE
  // =========================================================

  const [
    practiceMode,
    setPracticeMode,
  ] = useState(null);

  const [
    selectedPracticeDomain,
    setSelectedPracticeDomain,
  ] = useState(null);

  const [
    selectedPracticeTopic,
    setSelectedPracticeTopic,
  ] = useState(null);

  const [
    practiceRoundResult,
    setPracticeRoundResult,
  ] = useState(null);

  const [
    activePracticeSummary,
    setActivePracticeSummary,
  ] = useState(null);

  const [
    activeSubjectPracticeSummaries,
    setActiveSubjectPracticeSummaries,
  ] = useState([]);

  const [
    restoredPracticeAnswers,
    setRestoredPracticeAnswers,
  ] = useState({});

  // =========================================================
  // AUTH
  // =========================================================

  const {
    session,
    authReady,

    email,
    setEmail,

    password,
    setPassword,

    isAdmin,

    handleLogin,
    handleLogout,

    isPasswordRecovery,

    newPassword,
    setNewPassword,

    handleUpdatePassword,

    forgotEmail,
    setForgotEmail,

    handleForgotPassword,

    authMode,
    setAuthMode,

    signupUsername,
    setSignupUsername,

    signupFullName,
    setSignupFullName,

    handleSignup,
  } = useAuth();

  // =========================================================
  // SUBSCRIPTION / ENTITLEMENTS
  // =========================================================

  const {
    entitlements,
    loadingEntitlements,
  } = useEntitlements(
    session
  );

  // =========================================================
  // PREMIUM PAGE GUARD
  //
  // Prevent Free users from remaining on Premium-only pages
  // through refresh, saved navigation state, or direct
  // internal page navigation.
  // =========================================================

  useEffect(() => {
    if (!session?.user) {
      return;
    }

    if (loadingEntitlements) {
      return;
    }

    if (!entitlements) {
      return;
    }

    const premiumPages = [
      "learningCenter",
      "learningUnit",
      "learningTopic",

      "vocabulary",
      "vocabularyTopic",
      "vocabularySearch",
      "vocabularyFlashcards",
      "vocabularyMatching",
      "vocabularyQuiz",
      "vocabularyReviewDue",
    ];

    if (
      !entitlements.has_premium_access &&
      premiumPages.includes(page)
    ) {
      localStorage.setItem(
        "massage_app_page",
        "account"
      );

      setPage("account");
    }
  }, [
    page,
    session?.user,
    entitlements,
    loadingEntitlements,
  ]);

  // =========================================================
  // PRACTICE QUESTIONS
  // =========================================================

  const {
    practiceQuestions,
    loadingPracticeQuestions,
    fetchPracticeQuestions,
    fetchWrongQuestions,
    currentPracticeSessionId,
    startPracticeSession,
    resumePracticeSession,
    getActivePracticeSummary,
    getActiveSubjectPracticeSummaries,
    submittingPracticeAnswer,
    submitPracticeAnswer,
    completePracticeSession,
    abandonPracticeSession,
  } = usePracticeQuestions(session);


  // =========================================================
  // PRACTICE SUBJECTS
  // =========================================================

  const {
    practiceDomains,

    practiceTopics,

    loadingPracticeSubjects,

    fetchPracticeDomains,

    fetchPracticeTopics,

    clearPracticeTopics,
  } = usePracticeSubjects();


  // =========================================================
  // STUDENTS
  // =========================================================

  const {
    students,

    loadingStudents,

    fetchStudents,

    grantAllAccess,

    voidAccess,
  } = useStudents(
    isAdmin
  );


  // =========================================================
  // EXAM SESSION ADMIN
  // =========================================================

  const {
    examSets,

    examSessions,

    loadingExamSessions,

    fetchExamSets,

    fetchExamSessions,

    createExamSession,
  } = useExamSessions(
    isAdmin
  );


  // =========================================================
  // STUDENT OPEN EXAMS
  // =========================================================

  const {
    openExamSessions,

    loadingOpenExams,

    fetchOpenExamSessions,
  } = useOpenExamSessions(
    session
  );


  // =========================================================
  // EXAM QUESTIONS
  //
  // Still legacy exam_questions source for Class Exam.
  // Will later migrate to Master Question Bank.
  // =========================================================

  const {
    examQuestions,

    loadingExamQuestions,

    fetchExamQuestions,
  } = useExamQuestions();


  // =========================================================
  // EXAM ATTEMPT
  // =========================================================

  const {
    currentAttemptId,

    submittingExam,

    startExamAttempt,

    submitExamAttempt,

    viewReportAnswers,

    loadingViewReport,

    fetchAttemptAnswers,
  } = useExamAttempt(
    session
  );


  // =========================================================
  // EXAM RESULTS
  // =========================================================

  const {
    examResult,

    loadingExamResult,

    fetchExamResult,
  } = useExamResults();


  // =========================================================
  // TEACHER QUESTION NOTES
  // =========================================================

  const {
    selectedQuestionDetail,

    classNotes,

    loadingQuestionDetail,

    fetchQuestionDetail,

    saveDefaultTeachingNote,

    deleteDefaultTeachingNote,

    addClassNote,

    updateClassNote,

    deleteClassNote,

    closeQuestionDetail,
  } = useQuestionTeachingNotes();


  // =========================================================
  // STUDENT QUESTION NOTES
  // =========================================================

  const {
    studentNotes,

    newStudentNote,

    setNewStudentNote,

    loadingStudentNotes,

    fetchStudentNotes,

    addStudentNote,

    updateStudentNote,

    deleteStudentNote,
  } = useStudentQuestionNotes(
    session
  );


  // =========================================================
  // RETAKE PERMISSIONS
  // =========================================================

  const {
    loadingRetake,

    grantRetakePermission,
  } = useRetakePermissions();


  // =========================================================
  // COURSES / COHORTS
  // =========================================================

  const {
    courses,

    cohorts,

    loadingCourses,

    fetchCoursesAndCohorts,

    createCourse,

    createCohort,
  } = useCourses(
    isAdmin
  );


  const {
    cohortEnrollments,

    loadingCohortEnrollments,

    fetchCohortEnrollments,

    enrollStudentInCohort,

    updateCohortEnrollmentStatus,
  } = useCohortEnrollments(
    isAdmin
  );

  // =========================================================
  // WRONG QUESTION V2
  //
  // Supabase-based architecture.
  // Source of truth:
  // question_user_progress
  // =========================================================

  const {
    wrongQuestionSummary,

    loadingWrongQuestionSummary,

    fetchWrongQuestionSummary,
  } = useWrongQuestionProgress(
    session
  );

  // =========================================================
  // LEARNING DATA
  // =========================================================

  const {
    learningUnits,

    learningTopics,

    topicTranslations,

    contentBlocks,

    topicExercises,

    selectedTopicTree,

    loadingLearning,

    fetchLearningUnits,

    fetchUnitTopics,

    fetchTopicDetail,
  } = useLearning();

  useEffect(() => {
    if (page === "learningCenter") {
      fetchLearningUnits();
    }
  }, [language, page, fetchLearningUnits]);

  useEffect(() => {
    const refreshLearningLanguage = async () => {
      // Learning Unit page:
      // reload topic translations for the newly selected language
      if (
        page === "learningUnit" &&
        selectedLearningUnit?.id
      ) {
        await fetchUnitTopics(
          selectedLearningUnit.id
        );

        return;
      }

      // Learning Topic page:
      // first reload topic translations,
      // then reload content/exercise translations
      if (
        page === "learningTopic" &&
        selectedLearningUnit?.id &&
        selectedLearningTopic?.id
      ) {
        await fetchUnitTopics(
          selectedLearningUnit.id
        );

        await fetchTopicDetail(
          selectedLearningTopic.id
        );
      }
    };

    refreshLearningLanguage();

    // We intentionally refresh when the selected UI language changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);


  const {
    submittingLearningExercise,

    submitLearningExercise,
  } = useLearningExercises(
    session
  );


  const {
    fetchTopicProgress,

    markTopicReviewed,

    undoTopicReviewed,

    getTopicReviewStatus,

    syncLearningProgress,
  } = useLearningProgress(
    session
  );


  // =========================================================
  // LEARNING NAVIGATION STATE
  // =========================================================

  const [
    selectedLearningUnit,
    setSelectedLearningUnit,
  ] = useState(null);

  const [
    selectedLearningTopic,
    setSelectedLearningTopic,
  ] = useState(null);

  const [
    pendingRestoreTopic,
    setPendingRestoreTopic,
  ] = useState(null);

  const [
    restoringLearningPage,
    setRestoringLearningPage,
  ] = useState(true);


  // =========================================================
  // VOCABULARY DATA
  // =========================================================

  const {
    vocabularyTopics,

    vocabularyTerms,

    dueVocabularyTerms,

    vocabularyDifficultyStats,

    loadingVocabulary,

    loadingDueVocabulary,

    submittingVocabularyReview,

    fetchVocabularyTopics,

    fetchVocabularyByTopic,

    fetchDueVocabularyTerms,

    searchVocabulary,

    getTranslationsForTerm,

    submitVocabularyReview,

    getVocabularyProgressForTerm,

    getMasteryLabel,

    getVocabularyTopicStats,

    submitVocabularyMatchingResult,

    submitVocabularyQuizResult,
  } = useVocabulary();


  // =========================================================
  // VOCABULARY NAVIGATION STATE
  // =========================================================

  const [
    selectedVocabularyTopic,
    setSelectedVocabularyTopic,
  ] = useState(null);

  const [
    vocabularySearchTitle,
    setVocabularySearchTitle,
  ] = useState("");

  const [
    vocabularyLanguage,
    setVocabularyLanguage,
  ] = useState(() => {
    return (
      localStorage.getItem(
        "vocabularyLanguage"
      ) || "zh"
    );
  });

  const [
    pendingVocabularyLanguage,
    setPendingVocabularyLanguage,
  ] = useState(
    vocabularyLanguage
  );


  // =========================================================
  // END PART 1
  //
  // PART 2 starts with:
  // - Derived state
  // - Effects
  // - Restore logic
  // - Practice handlers
  // - Vocabulary handlers
  // =========================================================

  // =========================================================
  // DERIVED STATE — LEGACY QUESTION BANK
  // =========================================================

  const goBackToPracticeSubjects =
    async () => {
      localStorage.removeItem(
        "selected_practice_domain_id"
      );

      setSelectedPracticeDomain(
        null
      );

      setSelectedPracticeTopic(
        null
      );

      setActiveSubjectPracticeSummaries(
        []
      );

      await fetchPracticeDomains();

      setPage(
        "practiceSubjects"
      );
    };

  // =========================================================
  // DERIVED STATE — NEW PRACTICE
  // =========================================================

  const currentPracticeQuestion =
    practiceQuestions[currentIndex];


  // =========================================================
  // SAVE CURRENT PAGE
  // =========================================================

  useEffect(() => {
    if (!restoringLearningPage) {
      localStorage.setItem(
        "massage_app_page",
        page
      );
    }
  }, [
    page,
    restoringLearningPage,
  ]);


  // =========================================================
  // RESTORE LEARNING PAGE AFTER REFRESH
  // =========================================================

  useEffect(() => {
    if (!authReady) {
      return;
    }

    // ---------------------------------------------------------
    // WAIT FOR ENTITLEMENTS
    //
    // Logged-in users must finish loading subscription access
    // before we restore any saved page.
    // ---------------------------------------------------------

    if (
      session?.user &&
      loadingEntitlements
    ) {
      return;
    }

    if (
      session?.user &&
      !entitlements
    ) {
      return;
    }

    const restoreLearningNavigation =
      async () => {
        const savedPage =
          localStorage.getItem(
            "massage_app_page"
          );

        // -----------------------------------------------
        // PREMIUM RESTORE GUARD
        //
        // Prevent Free users from loading Premium data
        // during page restoration.
        // -----------------------------------------------

        const premiumRestorePages = [
          "learningCenter",
          "learningUnit",
          "learningTopic",

          "vocabulary",
          "vocabularyTopic",
          "vocabularySearch",
          "vocabularyFlashcards",
          "vocabularyMatching",
          "vocabularyQuiz",
          "vocabularyReviewDue",
        ];

        if (
          savedPage &&
          premiumRestorePages.includes(savedPage) &&
          !entitlements?.has_premium_access
        ) {
          localStorage.setItem(
            "massage_app_page",
            "account"
          );

          setPage("account");

          setRestoringLearningPage(false);

          return;
        }

        const savedUnitId =
          localStorage.getItem(
            "selected_learning_unit_id"
          );

        const savedTopicId =
          localStorage.getItem(
            "selected_learning_topic_id"
          );

        const learningPages = [
          "learningCenter",
          "learningUnit",
          "learningTopic",
        ];

        const simpleRestorePages = [
          "home",
          "account",
          "practiceHome",
          "practiceSubjects",
          "wrongQuestionHome",
          "vocabulary",
          "classExam",
          "admin",
        ];
        
        // -----------------------------------------------
        // RESTORE IN-PROGRESS PRACTICE ROUND
        // -----------------------------------------------
        // 1. Practice Quiz special restore
        if (
          savedPage === "practiceQuiz"
        ) {
          if (!session?.user?.id) {
            setPage("home");

            setRestoringLearningPage(
              false
            );

            return;
          }
          const savedPracticeSessionId =
            localStorage.getItem(
              "active_practice_session_id"
            );


          const restored =
            await restoreCurrentPracticeSession({
              practiceSessionId:
                savedPracticeSessionId ||
                null,
            });

          if (!restored) {

            // Remove stale exact-session pointer.
            localStorage.removeItem(
              "active_practice_session_id"
            );

            setPage(
              "practiceHome"
            );
          }

          setRestoringLearningPage(
            false
          );
          return;
        }

        // 1.1 Practice Topic Page - RESTORE PRACTICE TOPICS PAGE

        if (
          savedPage === "practiceTopics"
        ) {
          const savedDomainId =
            localStorage.getItem(
              "selected_practice_domain_id"
            );

          // No saved domain -> safely fall back
          if (!savedDomainId) {
            await fetchPracticeDomains();

            setPage(
              "practiceSubjects"
            );

            setRestoringLearningPage(
              false
            );

            return;
          }

          try {
            // ---------------------------------------------
            // Reload domains so we restore fresh DB data
            // instead of stale localStorage objects.
            // ---------------------------------------------

            const domains =
              await fetchPracticeDomains();

            const savedDomain =
              (domains || []).find(
                (domain) =>
                  domain.id ===
                  savedDomainId
              );

            if (!savedDomain) {
              throw new Error(
                "Saved practice domain no longer exists."
              );
            }

            setSelectedPracticeDomain(
              savedDomain
            );

            setSelectedPracticeTopic(
              null
            );

            const [
              ,
              activeSummaries,
            ] = await Promise.all([
              fetchPracticeTopics(
                savedDomain.id
              ),

              getActiveSubjectPracticeSummaries({
                domainId:
                  savedDomain.id,
              }),
            ]);

            setActiveSubjectPracticeSummaries(
              activeSummaries
            );

            setPage(
              "practiceTopics"
            );
          } catch (error) {
            console.error(
              "RESTORE PRACTICE TOPICS ERROR:",
              error
            );

            localStorage.removeItem(
              "selected_practice_domain_id"
            );

            await fetchPracticeDomains();

            setPage(
              "practiceSubjects"
            );
          }

          setRestoringLearningPage(
            false
          );

          return;
        }

        // -----------------------------------------------
        // RESTORE VOCABULARY TOPIC
        // -----------------------------------------------

        if (
          savedPage === "vocabularyTopic"
        ) {
          const savedVocabularyTopicId =
            localStorage.getItem(
              "selected_vocabulary_topic_id"
            );

          if (!savedVocabularyTopicId) {
            await fetchVocabularyTopics();

            setPage("vocabulary");

            setRestoringLearningPage(false);

            return;
          }

          try {
            const topics =
              await fetchVocabularyTopics();

            const savedVocabularyTopic =
              (topics || []).find(
                (topic) =>
                  topic.id ===
                  savedVocabularyTopicId
              );

            if (!savedVocabularyTopic) {
              localStorage.removeItem(
                "selected_vocabulary_topic_id"
              );

              setPage("vocabulary");

              setRestoringLearningPage(false);

              return;
            }

            setSelectedVocabularyTopic(
              savedVocabularyTopic
            );

            await fetchVocabularyByTopic(
              savedVocabularyTopic.id
            );

            setPage(
              "vocabularyTopic"
            );
          } catch (error) {
            console.error(
              "RESTORE VOCABULARY TOPIC ERROR:",
              error
            );

            localStorage.removeItem(
              "selected_vocabulary_topic_id"
            );

            setPage("vocabulary");
          }

          setRestoringLearningPage(false);

          return;
        }

        // 2. Simple pages
        if (
          savedPage &&
          simpleRestorePages.includes(savedPage)
        ) {
          // -----------------------------------------------
          // VOCABULARY HOME
          // -----------------------------------------------

          if (savedPage === "vocabulary") {
            await Promise.all([
              fetchVocabularyTopics(),
              fetchDueVocabularyTerms(),
            ]);
          }

          // -----------------------------------------------
          // PRACTICE HOME
          //
          // Refresh must reload active round status.
          // Otherwise activePracticeSummary resets to null.
          // -----------------------------------------------

          if (savedPage === "practiceHome") {
            const activeSummary =
              await getActivePracticeSummary();

            setActivePracticeSummary(
              activeSummary
            );
          }

          // -----------------------------------------------
          // PRACTICE SUBJECTS
          // -----------------------------------------------

          if (savedPage === "practiceSubjects") {
            await fetchPracticeDomains();
          }

          // -----------------------------------------------
          // WRONG QUESTION HOME
          // -----------------------------------------------

          if (
            savedPage ===
            "wrongQuestionHome"
          ) {

            await fetchWrongQuestionSummary();

            setPracticeMode(
              "wrong_review"
            );
          }

          setPage(savedPage);

          setRestoringLearningPage(false);

          return;
        }

        if (!learningPages.includes(savedPage)) {
          setRestoringLearningPage(false);
          return;
        }

        try {
          // -----------------------------------------------
          // RESTORE LEARNING CENTER
          // -----------------------------------------------

          if (
            savedPage ===
            "learningCenter"
          ) {
            await fetchLearningUnits();

            setPage(
              "learningCenter"
            );

            setRestoringLearningPage(
              false
            );

            return;
          }

          // -----------------------------------------------
          // LOAD UNIT
          // -----------------------------------------------

          const units =
            await fetchLearningUnits();

          const savedUnit =
            units.find(
              (unit) =>
                unit.id ===
                savedUnitId
            );

          if (!savedUnit) {
            localStorage.removeItem(
              "selected_learning_unit_id"
            );

            localStorage.removeItem(
              "selected_learning_topic_id"
            );

            setPage(
              "learningCenter"
            );

            setRestoringLearningPage(
              false
            );

            return;
          }

          setSelectedLearningUnit(
            savedUnit
          );

          const topics =
            await fetchUnitTopics(
              savedUnit.id
            );

          // -----------------------------------------------
          // RESTORE UNIT PAGE
          // -----------------------------------------------

          if (
            savedPage ===
            "learningUnit"
          ) {
            setPage(
              "learningUnit"
            );

            setRestoringLearningPage(
              false
            );

            return;
          }

          // -----------------------------------------------
          // RESTORE TOPIC PAGE
          // -----------------------------------------------

          if (
            savedPage ===
              "learningTopic" &&
            savedTopicId
          ) {
            const savedTopic =
              topics.find(
                (topic) =>
                  topic.id ===
                  savedTopicId
              );

            if (!savedTopic) {
              localStorage.removeItem(
                "selected_learning_topic_id"
              );

              setPage(
                "learningUnit"
              );

              setRestoringLearningPage(
                false
              );

              return;
            }

            setSelectedLearningTopic(
              savedTopic
            );

            setPendingRestoreTopic(
              savedTopic
            );

            return;
          }

          setPage(
            "learningUnit"
          );

          setRestoringLearningPage(
            false
          );
        } catch (error) {
          console.error(
            "RESTORE LEARNING PAGE ERROR:",
            error
          );

          setPage("home");

          setRestoringLearningPage(
            false
          );
        }
      };

    restoreLearningNavigation();

    // Restore after authentication + entitlement loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      authReady,
      session?.user?.id,
      loadingEntitlements,
      entitlements?.has_premium_access,
    ]);


  // =========================================================
  // FINISH TOPIC RESTORATION
  // =========================================================

  useEffect(() => {
    if (!pendingRestoreTopic) {
      return;
    }

    const topicIsLoaded =
      learningTopics.some(
        (topic) =>
          topic.id ===
          pendingRestoreTopic.id
      );

    if (!topicIsLoaded) {
      return;
    }

    const restoreTopicDetail =
      async () => {
        const topicToRestore =
          pendingRestoreTopic;

        setPendingRestoreTopic(
          null
        );

        try {
          await fetchTopicDetail(
            topicToRestore.id
          );

          setPage(
            "learningTopic"
          );
        } catch (error) {
          console.error(
            "RESTORE TOPIC DETAIL ERROR:",
            error
          );

          setPage(
            "learningUnit"
          );
        } finally {
          setRestoringLearningPage(
            false
          );
        }
      };

    restoreTopicDetail();
  }, [
    pendingRestoreTopic,
    learningTopics,
    fetchTopicDetail,
  ]);

  // =========================================================
  // VOCABULARY LANGUAGE
  // =========================================================

  const saveVocabularyLanguage =
    () => {
      setVocabularyLanguage(
        pendingVocabularyLanguage
      );

      localStorage.setItem(
        "vocabularyLanguage",
        pendingVocabularyLanguage
      );
    };


  // =========================================================
  // VOCABULARY NAVIGATION
  // =========================================================

  const openVocabulary = async () => {
    if (!entitlements?.can_use_vocabulary) {
      setPage("account");
      return;
    }

    await Promise.all([
      fetchVocabularyTopics(),
      fetchDueVocabularyTerms(),
    ]);

    setPage("vocabulary");
  };


  const openVocabularyTopic =
    async (topic) => {
      setSelectedVocabularyTopic(
        topic
      );

      localStorage.setItem(
        "selected_vocabulary_topic_id",
        topic.id
      );

      await fetchVocabularyByTopic(
        topic.id
      );

      setPage(
        "vocabularyTopic"
      );
    };


  const openVocabularySearchResults =
    (searchText) => {
      setSelectedVocabularyTopic(
        null
      );

      setVocabularySearchTitle(
        `Search Results: ${searchText}`
      );

      setPage(
        "vocabularySearch"
      );
    };


  const openVocabularyFlashcards =
    () => {
      setPage(
        "vocabularyFlashcards"
      );
    };


  const openVocabularyMatching =
    () => {
      setPage(
        "vocabularyMatching"
      );
    };


  const openVocabularyQuiz =
    () => {
      setPage(
        "vocabularyQuiz"
      );
    };


  const openVocabularyReviewDue =
    async () => {
      await fetchDueVocabularyTerms();

      setPage(
        "vocabularyReviewDue"
      );
    };


  // =========================================================
  // PRACTICE NAVIGATION HELPERS
  // =========================================================
  const resetPracticeNavigation =
    () => {
      setCurrentIndex(0);

      setPracticeRoundResult(
        null
      );
    };


  const goNextPractice =
    () => {
      if (
        currentIndex <
        practiceQuestions.length - 1
      ) {
        setCurrentIndex(
          (prev) => prev + 1
        );
      }
    };


  const goPreviousPractice =
    () => {
      if (currentIndex > 0) {
        setCurrentIndex(
          (prev) => prev - 1
        );
      }
    };

  // =========================================================
  // OPEN WRONG QUESTION CENTER
  //
  // Loads the student's current Wrong Question V2 summary
  // from Supabase before opening the page.
  // =========================================================

  const openWrongQuestionCenter =
    async () => {

      if (!session?.user?.id) {
        alert(
          "Please log in first."
        );

        return;
      }


      // -------------------------------------------------------
      // Load current Wrong Question statistics
      // -------------------------------------------------------

      const summary =
        await fetchWrongQuestionSummary();


      if (!summary) {
        alert(
          "Unable to load your wrong questions."
        );

        return;
      }


      // -------------------------------------------------------
      // Reset shared practice navigation state
      //
      // Wrong Question Review will later reuse
      // PracticeQuestionPage.
      // -------------------------------------------------------

      resetPracticeNavigation();

      setPracticeMode(
        "wrong_review"
      );

      setSelectedPracticeDomain(
        null
      );

      setSelectedPracticeTopic(
        null
      );


      // -------------------------------------------------------
      // Open Wrong Question Center
      // -------------------------------------------------------

      setPage(
        "wrongQuestionHome"
      );
    };

  // =========================================================
  // START DUE WRONG QUESTION REVIEW
  //
  // 1. Get highest-priority due wrong questions.
  // 2. Create a normal frozen practice session.
  // 3. Reuse PracticeQuestionPage.
  // =========================================================

  const startDueWrongQuestionReview =
    async () => {

      if (!session?.user?.id) {
        alert(
          "Please log in first."
        );

        return;
      }

      // =======================================================
      // 1. TRY TO RESUME EXISTING WRONG REVIEW
      //
      // If the student previously left an unfinished
      // Wrong Question Review, continue that frozen round
      // instead of creating another session.
      // =======================================================

      const resumed =
        await restoreCurrentPracticeSession({
          practiceModeFilter:
            "wrong_review",
        });

      if (resumed) {
        return;
      }

      // =======================================================
      // 1.1. GET UP TO 10 DUE WRONG QUESTIONS
      // =======================================================

      const questions =
        await fetchWrongQuestions({
          limit: 10,
          filter: "due",
        });

      if (!questions?.length) {
        alert(
          "No questions are currently due for review."
        );

        // Refresh summary in case it changed.
        await fetchWrongQuestionSummary();

        return;
      }


      // =======================================================
      // 2. CREATE FROZEN WRONG REVIEW SESSION
      //
      // Reuses existing practice_sessions +
      // practice_session_questions architecture.
      // =======================================================

      const practiceSessionId =
        await startPracticeSession({
          practiceMode:
            "wrong_review",

          domainId:
            null,

          topicId:
            null,

          difficultyMode:
            "review",

          questions,
        });


      if (!practiceSessionId) {
        return;
      }


      // =======================================================
      // 3. RESET LOCAL PRACTICE STATE
      // =======================================================

      setPracticeMode(
        "wrong_review"
      );

      setSelectedPracticeDomain(
        null
      );

      setSelectedPracticeTopic(
        null
      );

      resetPracticeNavigation();

      setRestoredPracticeAnswers(
        {}
      );


      // =======================================================
      // 4. ENTER EXISTING PRACTICE QUESTION PAGE
      // =======================================================

      setPage(
        "practiceQuiz"
      );
    };

  // =========================================================
  // START ACTIVE WRONG QUESTION PRACTICE
  //
  // Allows the student to actively practice wrong questions
  // even when those questions are not currently due.
  //
  // This is different from Due Review:
  // - Due Review follows the spaced-review schedule.
  // - Active Practice allows extra practice anytime.
  // =========================================================

  const startActiveWrongQuestionPractice =
    async () => {

      if (!session?.user?.id) {
        alert(
          "Please log in first."
        );

        return;
      }


      // =======================================================
      // 1. GET UP TO 10 ACTIVE WRONG QUESTIONS
      // =======================================================

      const questions =
        await fetchWrongQuestions({
          limit: 10,
          filter: "active",
        });


      if (!questions?.length) {
        alert(
          "You do not have any active wrong questions to practice."
        );

        await fetchWrongQuestionSummary();

        return;
      }


      // =======================================================
      // 2. CREATE NEW WRONG PRACTICE SESSION
      //
      // We use a separate practice_mode so this round can be
      // distinguished from scheduled Due Review.
      // =======================================================

      const practiceSessionId =
        await startPracticeSession({
          practiceMode:
            "wrong_active",

          domainId: null,
          topicId: null,

          difficultyMode:
            "review",

          questions,
        });


      if (!practiceSessionId) {
        return;
      }


      // =======================================================
      // 3. RESET PRACTICE UI
      // =======================================================

      setPracticeMode(
        "wrong_active"
      );

      setSelectedPracticeDomain(
        null
      );

      setSelectedPracticeTopic(
        null
      );

      resetPracticeNavigation();

      setRestoredPracticeAnswers(
        {}
      );


      // =======================================================
      // 4. ENTER PRACTICE PAGE
      // =======================================================

      setPage(
        "practiceQuiz"
      );
    };

  // =========================================================
  // OPEN PRACTICE HUB
  // =========================================================

  const openPractice =
    async () => {
      setPracticeMode(null);

      setSelectedPracticeDomain(
        null
      );

      setSelectedPracticeTopic(
        null
      );

      resetPracticeNavigation();

      const activeSummary =
        await getActivePracticeSummary();

      setActivePracticeSummary(
        activeSummary
      );

      setPage(
        "practiceHome"
      );
    };

  // =========================================================
  // START SMART / ADAPTIVE PRACTICE
  // =========================================================

  const startRandomPractice =
    async () => {
      // -----------------------------------------------------
      // First try to continue unfinished Smart Practice
      // -----------------------------------------------------

      const resumed =
        await restoreCurrentPracticeSession({
          practiceModeFilter:
            "adaptive",
        });

      if (resumed) {
        return;
      }

      // -----------------------------------------------------
      // No unfinished round -> create a new adaptive round
      // -----------------------------------------------------
      const questions =
        await fetchPracticeQuestions({
          limit: 10,
        });

      if (!questions?.length) {
        alert(
          "No practice questions are available."
        );

        return;
      }

      const practiceSessionId =
        await startPracticeSession({
          practiceMode: "adaptive",
          domainId: null,
          topicId: null,
          difficultyMode: "adaptive",
          questions,
        });

      if (
        !practiceSessionId
      ) {
        return;
      }

      setPracticeMode(
        "random"
      );

      setSelectedPracticeDomain(
        null
      );

      setSelectedPracticeTopic(
        null
      );

      resetPracticeNavigation();

      setPage(
        "practiceQuiz"
      );
    };

  // =========================================================
  // START NEW SMART PRACTICE ROUND
  // =========================================================
  const startNewSmartPracticeRound =
    async () => {
      const activeSummary =
        await getActivePracticeSummary();

      if (
        activeSummary?.sessionId
      ) {
        const abandoned =
          await abandonPracticeSession(
            activeSummary.sessionId
          );

        if (!abandoned) {
          alert(
            "Unable to start a new round."
          );

          return;
        }
      }

      // Clear old local practice state
      resetPracticeNavigation();

      setRestoredPracticeAnswers(
        {}
      );

      setActivePracticeSummary(
        null
      );

      // Run adaptive engine again
      const questions =
        await fetchPracticeQuestions({
          limit: 10,
        });

      if (!questions?.length) {
        alert(
          "No practice questions are available."
        );

        return;
      }

      const practiceSessionId =
        await startPracticeSession({
          practiceMode:
            "adaptive",

          domainId: null,

          topicId: null,

          difficultyMode:
            "adaptive",

          questions,
        });

      if (!practiceSessionId) {
        return;
      }

      setPracticeMode(
        "random"
      );

      setSelectedPracticeDomain(
        null
      );

      setSelectedPracticeTopic(
        null
      );

      setCurrentIndex(0);

      setPage(
        "practiceQuiz"
      );
    };

  // =========================================================
  // OPEN PRACTICE BY SUBJECT
  // =========================================================

  const openSubjectPractice =
    async () => {
      setPracticeMode(
        "subject"
      );

      setSelectedPracticeDomain(
        null
      );

      setSelectedPracticeTopic(
        null
      );

      clearPracticeTopics();

      await fetchPracticeDomains();

      setPage(
        "practiceSubjects"
      );
    };

  // =========================================================
  // OPEN PRACTICE DOMAIN
  // =========================================================

  const openPracticeDomain =
    async (domain) => {
      setSelectedPracticeDomain(
        domain
      );

      localStorage.setItem(
        "selected_practice_domain_id",
        domain.id
      );

      setSelectedPracticeTopic(
        null
      );

      const [
        topics,
        activeSummaries,
      ] = await Promise.all([
        fetchPracticeTopics(
          domain.id
        ),

        getActiveSubjectPracticeSummaries({
          domainId:
            domain.id,
        }),
      ]);

      setActiveSubjectPracticeSummaries(
        activeSummaries
      );

      setPage(
        "practiceTopics"
      );

      return topics;
    };

  // =========================================================
  // START PRACTICE BY TOPIC
  // =========================================================

  const openPracticeTopic =
    async (topic) => {
      setSelectedPracticeTopic(
        topic
      );

      // =====================================================
      // 1. TRY TO RESUME THIS EXACT SUBJECT / TOPIC
      // =====================================================

      const resumed =
        await restoreCurrentPracticeSession({
          practiceModeFilter:
            "subject",

          domainId:
            selectedPracticeDomain?.id ||
            null,

          topicId:
            topic.id,
        });

      if (resumed) {
        setPracticeMode(
          "subject"
        );

        return;
      }


      // =====================================================
      // 2. NO ACTIVE ROUND -> BUILD A NEW ADAPTIVE ROUND
      // =====================================================

      const questions =
        await fetchPracticeQuestions({
          limit: 10,

          domainId:
            selectedPracticeDomain?.id,

          topicId:
            topic.id,
        });

      if (!questions?.length) {
        alert(
          "No practice questions are available for this topic."
        );

        return;
      }


      // =====================================================
      // 3. CREATE SESSION + FREEZE SAME 10 QUESTIONS
      // =====================================================

      const practiceSessionId =
        await startPracticeSession({
          practiceMode:
            "subject",

          domainId:
            selectedPracticeDomain?.id ||
            null,

          topicId:
            topic.id,

          difficultyMode:
            "adaptive",

          questions,
        });

      if (!practiceSessionId) {
        return;
      }


      // =====================================================
      // 4. ENTER NEW SUBJECT ROUND
      // =====================================================

      setPracticeMode(
        "subject"
      );

      resetPracticeNavigation();

      setPage(
        "practiceQuiz"
      );
    };
  
  // =========================================================
  // START NEW SUBJECT PRACTICE ROUND
  // =========================================================

  const startNewSubjectPracticeRound =
    async (
      topic,
      activeSummary
    ) => {
      if (!topic) {
        return;
      }

      // -----------------------------------------------
      // 1. Abandon existing round for this topic
      // -----------------------------------------------

      if (activeSummary?.sessionId) {
        const abandoned =
          await abandonPracticeSession(
            activeSummary.sessionId
          );

        if (!abandoned) {
          alert(
            "Unable to start a new practice round."
          );

          return;
        }
      }

      // -----------------------------------------------
      // 2. Clear current local practice state
      // -----------------------------------------------

      resetPracticeNavigation();

      setSelectedPracticeTopic(
        topic
      );

      // -----------------------------------------------
      // 3. Re-run adaptive selection inside this topic
      // -----------------------------------------------

      const questions =
        await fetchPracticeQuestions({
          limit: 10,

          domainId:
            selectedPracticeDomain?.id ||
            null,

          topicId:
            topic.id,
        });

      if (!questions?.length) {
        alert(
          "No practice questions are available for this topic."
        );

        return;
      }

      // -----------------------------------------------
      // 4. Create new frozen subject session
      // -----------------------------------------------

      const practiceSessionId =
        await startPracticeSession({
          practiceMode:
            "subject",

          domainId:
            selectedPracticeDomain?.id ||
            null,

          topicId:
            topic.id,

          difficultyMode:
            "adaptive",

          questions,
        });

      if (!practiceSessionId) {
        return;
      }

      // -----------------------------------------------
      // 5. Enter new round
      // -----------------------------------------------

      setPracticeMode(
        "subject"
      );

      setCurrentIndex(0);

      setPage(
        "practiceQuiz"
      );
    };

  // =========================================================
  // COMPLETE PRACTICE ROUND
  // =========================================================

  const finishPracticeRound =
    async () => {
      if (
        !currentPracticeSessionId
      ) {
        return;
      }

      const result =
        await completePracticeSession(
          currentPracticeSessionId
        );

      if (!result) {
        alert(
          "Unable to complete this practice round."
        );

        return;
      }

      setPracticeRoundResult(
        result
      );

      setPage(
        "practiceRoundComplete"
      );
    };


  // =========================================================
  // CONTINUE AFTER PRACTICE ROUND
  // =========================================================

  const continuePracticeRound =
    () => {

      // -------------------------------------------------------
      // SUBJECT PRACTICE
      // -------------------------------------------------------

      if (
        practiceMode ===
          "subject" &&
        selectedPracticeTopic
      ) {

        openPracticeTopic(
          selectedPracticeTopic
        );

        return;
      }


      // -------------------------------------------------------
      // WRONG QUESTION REVIEW
      //
      // Return to Wrong Question Center so the summary can
      // refresh after the completed review round.
      // -------------------------------------------------------

      if (
        practiceMode === "wrong_review" ||
        practiceMode === "wrong_active"
      ) {

        openWrongQuestionCenter();

        return;
      }


      // -------------------------------------------------------
      // SMART PRACTICE
      // -------------------------------------------------------

      startRandomPractice();
    };

    // =========================================================
    // FINISH PRACTICE FLOW
    //
    // Routes the student back to the correct home page
    // based on the current practice mode.
    // =========================================================

    const finishPracticeFlow =
      async () => {

        if (
          practiceMode === "wrong_review" ||
          practiceMode === "wrong_active"
        ) {

          await openWrongQuestionCenter();

          return;
        }

        setPage(
          "practiceHome"
        );
      };

    // =========================================================
    // RESTORE EXISTING PRACTICE ROUND
    // =========================================================

    const restoreCurrentPracticeSession =
      async ({
        practiceSessionId = null,
        practiceModeFilter = null,
        domainId = null,
        topicId = null,
      } = {}) => {
        const restored =
          await resumePracticeSession({
            practiceSessionId,
            practiceMode: practiceModeFilter,
            domainId,
            topicId,
          });

        if (!restored) {
          return false;
        }

        // -----------------------------------------------------
        // Restore previously submitted answers
        // -----------------------------------------------------

        const answerMap = {};

        (restored.answers || []).forEach(
          (answer) => {
            answerMap[
              answer.question_id
            ] = {
              selectedOptionId:
                answer.selected_option_id,

              isCorrect:
                answer.is_correct,

              correctOptionId:
                answer.correct_option_id,

              answeredAt:
                answer.answered_at,
            };
          }
        );

        setRestoredPracticeAnswers(
          answerMap
        );

        // -----------------------------------------------------
        // Restore the student's position
        //
        // IMPORTANT:
        // We use the existing shared currentIndex.
        // There is NO separate practiceIndex.
        // -----------------------------------------------------

        setCurrentIndex(
          restored.nextQuestionIndex
        );

        // -----------------------------------------------------
        // Restore practice mode
        //
        // Database practice_mode:
        // adaptive     -> UI mode "random"
        // subject      -> UI mode "subject"
        // wrong_review -> UI mode "wrong_review"
        // -----------------------------------------------------

        const restoredPracticeMode =
          restored.session?.practice_mode;


        if (
          restoredPracticeMode ===
          "subject"
        ) {

          setPracticeMode(
            "subject"
          );

        } else if (
          restoredPracticeMode ===
          "wrong_review"
        ) {

          setPracticeMode(
            "wrong_review"
          );

        } else if (
          restoredPracticeMode ===
          "wrong_active"
        ) {

          setPracticeMode(
            "wrong_active"
          );

        } else {

          setPracticeMode(
            "random"
          );
        }

        setPracticeRoundResult(
          null
        );

        setPage(
          "practiceQuiz"
        );

        return true;
      };

  // =========================================================
  // LEARNING STORAGE HELPERS
  // =========================================================

  const clearLearningNavigationStorage =
    () => {
      localStorage.removeItem(
        "selected_learning_unit_id"
      );

      localStorage.removeItem(
        "selected_learning_topic_id"
      );
    };


  // =========================================================
  // OPEN LEARNING CENTER
  // =========================================================

  const openLearningCenter =
    async () => {

      if (
        !entitlements?.can_use_learning_center
      ) {
        setPage("account");
        return;
      }

      await fetchLearningUnits();

      setSelectedLearningUnit(
        null
      );

      setSelectedLearningTopic(
        null
      );

      clearLearningNavigationStorage();

      setPage(
        "learningCenter"
      );
    };


  // =========================================================
  // OPEN LEARNING UNIT
  // =========================================================

  const openLearningUnit =
    async (
      unit
    ) => {
      setSelectedLearningUnit(
        unit
      );

      setSelectedLearningTopic(
        null
      );

      localStorage.setItem(
        "selected_learning_unit_id",
        unit.id
      );

      localStorage.removeItem(
        "selected_learning_topic_id"
      );

      await fetchUnitTopics(
        unit.id
      );

      setPage(
        "learningUnit"
      );
    };


  // =========================================================
  // OPEN LEARNING TOPIC
  // =========================================================

  const openLearningTopic =
    async (
      topic
    ) => {
      setSelectedLearningTopic(
        topic
      );

      localStorage.setItem(
        "selected_learning_topic_id",
        topic.id
      );

      await fetchTopicDetail(
        topic.id
      );

      setPage(
        "learningTopic"
      );
    };


  // =========================================================
  // LEARNING BACK NAVIGATION
  // =========================================================

  const goBackToLearningUnit =
    () => {
      setSelectedLearningTopic(
        null
      );

      localStorage.removeItem(
        "selected_learning_topic_id"
      );

      setPage(
        "learningUnit"
      );
    };


  const goBackToLearningCenter =
    () => {
      setSelectedLearningUnit(
        null
      );

      setSelectedLearningTopic(
        null
      );

      clearLearningNavigationStorage();

      setPage(
        "learningCenter"
      );
    };


  // =========================================================
  // HOME RESET
  //
  // Centralized home navigation cleanup.
  // =========================================================

  const goBackToHome =
    () => {
      setPage(
        "home"
      );

      localStorage.setItem(
        "massage_app_page",
        "home"
      );


      // -----------------------------------------------------
      // LEARNING
      // -----------------------------------------------------

      clearLearningNavigationStorage();

      setSelectedLearningUnit(
        null
      );

      setSelectedLearningTopic(
        null
      );

      // -----------------------------------------------------
      // PRACTICE
      // -----------------------------------------------------

      setPracticeMode(
        null
      );

      setSelectedPracticeDomain(
        null
      );

      setSelectedPracticeTopic(
        null
      );

      setPracticeRoundResult(
        null
      );


      // -----------------------------------------------------
      // SHARED QUESTION STATE
      // -----------------------------------------------------

      setCurrentIndex(0);
    };

  // =========================================================
  // PASSWORD RECOVERY GUARD
  //
  // Supabase recovery links create a temporary authenticated
  // session. Do not allow that recovery session to enter the
  // normal application before a new password is set.
  // =========================================================

  if (isPasswordRecovery) {
    return (
      <div
        style={{
          ...styles.page,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "460px",
            background: "#ffffff",
            borderRadius: "20px",
            padding: "40px",
            boxShadow:
              "0 12px 40px rgba(15, 23, 42, 0.10)",
            border: "1px solid #e8edf5",
            boxSizing: "border-box",
          }}
        >
          {/* Title */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "32px",
            }}
          >
            <div
              style={{
                fontSize: "34px",
                marginBottom: "14px",
              }}
            >
              🔐
            </div>

            <h1
              style={{
                margin: "0 0 10px 0",
                fontSize: "30px",
                fontWeight: "700",
                color: "#1e293b",
              }}
            >
              Reset Password
            </h1>

            <p
              style={{
                margin: 0,
                fontSize: "15px",
                lineHeight: "1.6",
                color: "#64748b",
              }}
            >
              Create a new password for your account.
            </p>
          </div>

          {/* Password */}
          <div
            style={{
              marginBottom: "22px",
            }}
          >
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#334155",
              }}
            >
              New Password
            </label>

            <input
              type="password"
              placeholder="Enter your new password"
              value={newPassword}
              onChange={(e) =>
                setNewPassword(e.target.value)
              }
              style={{
                width: "100%",
                height: "50px",
                padding: "0 15px",
                fontSize: "16px",
                border: "1px solid #cbd5e1",
                borderRadius: "10px",
                outline: "none",
                boxSizing: "border-box",
                background: "#ffffff",
              }}
            />
          </div>

          {/* Button */}
          <button
            onClick={handleUpdatePassword}
            disabled={!newPassword}
            style={{
              width: "100%",
              height: "50px",
              border: "none",
              borderRadius: "10px",
              fontSize: "16px",
              fontWeight: "700",
              cursor: newPassword
                ? "pointer"
                : "not-allowed",

              background: newPassword
                ? "#2563eb"
                : "#cbd5e1",

              color: "#ffffff",
            }}
          >
            Update Password
          </button>

          <p
            style={{
              margin:
                "20px 0 0 0",
              textAlign: "center",
              fontSize: "13px",
              lineHeight: "1.5",
              color: "#94a3b8",
            }}
          >
            After updating your password,
            you will be asked to sign in again.
          </p>
        </div>
      </div>
    );
  }

  // =========================================================
  // RESTORE SCREEN
  // =========================================================

  if (
    restoringLearningPage
  ) {
    return (
      <div
        style={
          styles.page
        }
      >
        <div
          style={
            styles.container
          }
        >
          <div
            style={
              styles.emptyBox
            }
          >
            Restoring your learning session...
          </div>
        </div>
      </div>
    );
  }


  // =========================================================
  // END PART 3
  //
  // PART 4 contains:
  // - Full render
  // - Home
  // - Admin
  // - Class Exam
  // - Learning
  // - Vocabulary
  // - New Practice
  // - Legacy Quiz
  // - Closing App component
  // =========================================================
  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div style={styles.page}>

      <div style={styles.globalLanguageBar}>
        <LanguageSelector />
      </div>

      <div style={styles.container}>
        {session && (
          <h1 style={styles.mainTitle}>
            {t("home.appTitle")}
          </h1>
        )}


        {/* ===================================================
            HOME
        =================================================== */}

        {page === "home" && (
          <HomePage
            session={session}

            entitlements={entitlements}

            loadingEntitlements={
              loadingEntitlements
            }

            email={email}

            setEmail={setEmail}

            password={password}

            setPassword={
              setPassword
            }

            isAdmin={isAdmin}

            handleLogin={
              handleLogin
            }

            handleLogout={
              handleLogout
            }

            openVocabulary={
              openVocabulary
            }

            openPractice={
              openPractice
            }

            openWrongQuestionCenter={
              openWrongQuestionCenter
            }

            setPage={
              setPage
            }

            styles={
              styles
            }

            isPasswordRecovery={
              isPasswordRecovery
            }

            newPassword={
              newPassword
            }

            setNewPassword={
              setNewPassword
            }

            handleUpdatePassword={
              handleUpdatePassword
            }

            forgotEmail={
              forgotEmail
            }

            setForgotEmail={
              setForgotEmail
            }

            handleForgotPassword={
              handleForgotPassword
            }

            authMode={
              authMode
            }

            setAuthMode={
              setAuthMode
            }

            signupUsername={
              signupUsername
            }

            setSignupUsername={
              setSignupUsername
            }

            signupFullName={
              signupFullName
            }

            setSignupFullName={
              setSignupFullName
            }

            handleSignup={
              handleSignup
            }

            openLearningCenter={
              openLearningCenter
            }
          />
        )}

        {/* ===================================================
            ACCOUNT / SUBSCRIPTION
        =================================================== */}

        {page === "account" && (
          <AccountPage
            session={session}
            entitlements={entitlements}
            loadingEntitlements={loadingEntitlements}
            startSubscriptionCheckout={startSubscriptionCheckout}
            openBillingPortal={openBillingPortal}
            goBackToHome={goBackToHome}
          />
        )}

        {/* ===================================================
            FOOTER - CONTACT US, TERM OF USE, PRIVACY
        =================================================== */}

        {page === "contact" && (
          <ContactPage
            session={session}
            goBackToHome={goBackToHome}
          />
        )}

        {page === "terms" && (
          <TermsPage
            goBackToHome={goBackToHome}
          />
        )}

        {page === "privacy" && (
          <PrivacyPage
            goBackToHome={goBackToHome}
          />
        )}

        {/* ===================================================
            ADMIN DASHBOARD
        =================================================== */}

        {page === "admin" &&
          isAdmin && (
            <AdminDashboard
              students={
                students
              }

              loadingStudents={
                loadingStudents
              }

              fetchStudents={
                fetchStudents
              }

              grantAllAccess={
                grantAllAccess
              }

              voidAccess={
                voidAccess
              }

              examSessions={
                examSessions
              }

              loadingExamSessions={
                loadingExamSessions
              }

              fetchExamSessions={
                fetchExamSessions
              }

              examSets={
                examSets
              }

              fetchExamSets={
                fetchExamSets
              }

              createExamSession={
                createExamSession
              }

              examResult={
                examResult
              }

              loadingExamResult={
                loadingExamResult
              }

              fetchExamResult={
                fetchExamResult
              }

              selectedQuestionDetail={
                selectedQuestionDetail
              }

              classNotes={
                classNotes
              }

              loadingQuestionDetail={
                loadingQuestionDetail
              }

              fetchQuestionDetail={
                fetchQuestionDetail
              }

              addClassNote={
                addClassNote
              }

              closeQuestionDetail={
                closeQuestionDetail
              }

              saveDefaultTeachingNote={
                saveDefaultTeachingNote
              }

              deleteDefaultTeachingNote={
                deleteDefaultTeachingNote
              }

              updateClassNote={
                updateClassNote
              }

              deleteClassNote={
                deleteClassNote
              }

              loadingRetake={
                loadingRetake
              }

              grantRetakePermission={
                grantRetakePermission
              }

              courses={
                courses
              }

              cohorts={
                cohorts
              }

              loadingCourses={
                loadingCourses
              }

              fetchCoursesAndCohorts={
                fetchCoursesAndCohorts
              }

              createCourse={
                createCourse
              }

              createCohort={
                createCohort
              }

              cohortEnrollments={
                cohortEnrollments
              }

              loadingCohortEnrollments={
                loadingCohortEnrollments
              }

              fetchCohortEnrollments={
                fetchCohortEnrollments
              }

              enrollStudentInCohort={
                enrollStudentInCohort
              }

              updateCohortEnrollmentStatus={
                updateCohortEnrollmentStatus
              }

              goBackToHome={
                goBackToHome
              }

              styles={styles}
            />
          )}


        {/* ===================================================
            CLASS EXAM
        =================================================== */}

        {page === "classExam" && (
          <ClassExamPage
            openExamSessions={
              openExamSessions
            }

            loadingOpenExams={
              loadingOpenExams
            }

            fetchOpenExamSessions={
              fetchOpenExamSessions
            }

            examQuestions={
              examQuestions
            }

            loadingExamQuestions={
              loadingExamQuestions
            }

            fetchExamQuestions={
              fetchExamQuestions
            }

            currentAttemptId={
              currentAttemptId
            }

            submittingExam={
              submittingExam
            }

            startExamAttempt={
              startExamAttempt
            }

            submitExamAttempt={
              submitExamAttempt
            }

            viewReportAnswers={
              viewReportAnswers
            }

            loadingViewReport={
              loadingViewReport
            }

            fetchAttemptAnswers={
              fetchAttemptAnswers
            }

            studentNotes={
              studentNotes
            }

            newStudentNote={
              newStudentNote
            }

            setNewStudentNote={
              setNewStudentNote
            }

            loadingStudentNotes={
              loadingStudentNotes
            }

            fetchStudentNotes={
              fetchStudentNotes
            }

            addStudentNote={
              addStudentNote
            }

            updateStudentNote={
              updateStudentNote
            }

            deleteStudentNote={
              deleteStudentNote
            }

            goBackToHome={
              goBackToHome
            }

            styles={styles}
          />
        )}


        {/* ===================================================
            LEARNING CENTER
        =================================================== */}

        {page === "learningCenter" && (
          <LearningCenterPage
            learningUnits={
              learningUnits
            }

            loadingLearning={
              loadingLearning
            }

            openUnit={
              openLearningUnit
            }

            goBackToHome={
              goBackToHome
            }

            styles={styles}
          />
        )}


        {/* ===================================================
            LEARNING UNIT
        =================================================== */}

        {page === "learningUnit" &&
          selectedLearningUnit && (
            <LearningUnitPage
              unit={
                selectedLearningUnit
              }

              topics={
                learningTopics
              }

              translations={
                topicTranslations
              }

              loadingLearning={
                loadingLearning
              }

              openTopic={
                openLearningTopic
              }

              goBack={
                goBackToLearningCenter
              }

              styles={styles}
            />
          )}


        {/* ===================================================
            LEARNING TOPIC
        =================================================== */}

        {page === "learningTopic" &&
          selectedLearningTopic && (
            <LearningTopicPage
              topic={
                selectedLearningTopic
              }

              translation={
                topicTranslations.find(
                  (translation) =>
                    translation.topic_id ===
                      selectedLearningTopic.id &&
                    translation.language_code ===
                      "zh"
                )?.translation
              }

              topicTranslations={
                topicTranslations
              }

              selectedTopicTree={
                selectedTopicTree
              }

              contentBlocks={
                contentBlocks
              }

              topicExercises={
                topicExercises
              }

              submittingLearningExercise={
                submittingLearningExercise
              }

              submitLearningExercise={
                submitLearningExercise
              }

              fetchTopicProgress={
                fetchTopicProgress
              }

              markTopicReviewed={
                markTopicReviewed
              }

              undoTopicReviewed={
                undoTopicReviewed
              }

              getTopicReviewStatus={
                getTopicReviewStatus
              }

              syncLearningProgress={
                syncLearningProgress
              }

              goBack={
                goBackToLearningUnit
              }

              styles={styles}
            />
          )}


        {/* ===================================================
            VOCABULARY HOME
        =================================================== */}

        {page === "vocabulary" && (
          <VocabularyHomePage
            vocabularyTopics={
              vocabularyTopics
            }

            loadingVocabulary={
              loadingVocabulary
            }

            openTopic={
              openVocabularyTopic
            }

            searchVocabulary={
              searchVocabulary
            }

            openSearchResults={
              openVocabularySearchResults
            }

            openReviewDue={
              openVocabularyReviewDue
            }

            dueCount={
              dueVocabularyTerms.length
            }

            loadingDueVocabulary={
              loadingDueVocabulary
            }

            goBackToHome={
              goBackToHome
            }

            styles={styles}
          />
        )}


        {/* ===================================================
            VOCABULARY REVIEW DUE
        =================================================== */}

        {page === "vocabularyReviewDue" && (
          <VocabularyFlashcards
            terms={
              dueVocabularyTerms
            }

            getTranslationsForTerm={
              getTranslationsForTerm
            }

            submitVocabularyReview={
              submitVocabularyReview
            }

            submittingVocabularyReview={
              submittingVocabularyReview
            }

            vocabularyLanguage={
              vocabularyLanguage
            }

            goBack={() =>
              setPage(
                "vocabulary"
              )
            }

            styles={styles}
          />
        )}


        {/* ===================================================
            VOCABULARY TOPIC
        =================================================== */}

        {page === "vocabularyTopic" &&
          selectedVocabularyTopic && (
            <VocabularyTopicPage
              topic={
                selectedVocabularyTopic
              }

              terms={
                vocabularyTerms
              }

              getTranslationsForTerm={
                getTranslationsForTerm
              }

              getVocabularyProgressForTerm={
                getVocabularyProgressForTerm
              }

              getMasteryLabel={
                getMasteryLabel
              }

              getVocabularyTopicStats={
                getVocabularyTopicStats
              }

              loadingVocabulary={
                loadingVocabulary
              }

              openFlashcards={
                openVocabularyFlashcards
              }

              openQuiz={
                openVocabularyQuiz
              }

              vocabularyLanguage={
                vocabularyLanguage
              }

              pendingVocabularyLanguage={
                pendingVocabularyLanguage
              }

              setPendingVocabularyLanguage={
                setPendingVocabularyLanguage
              }

              saveVocabularyLanguage={
                saveVocabularyLanguage
              }

              openMatching={
                openVocabularyMatching
              }

              goBack={() =>
                setPage(
                  "vocabulary"
                )
              }

              styles={styles}
            />
          )}


        {/* ===================================================
            VOCABULARY SEARCH
        =================================================== */}

        {page === "vocabularySearch" && (
          <VocabularyTopicPage
            topic={null}

            terms={
              vocabularyTerms
            }

            getTranslationsForTerm={
              getTranslationsForTerm
            }

            getVocabularyProgressForTerm={
              getVocabularyProgressForTerm
            }

            getMasteryLabel={
              getMasteryLabel
            }

            loadingVocabulary={
              loadingVocabulary
            }

            titleOverride={
              vocabularySearchTitle
            }

            vocabularyLanguage={
              vocabularyLanguage
            }

            pendingVocabularyLanguage={
              pendingVocabularyLanguage
            }

            setPendingVocabularyLanguage={
              setPendingVocabularyLanguage
            }

            saveVocabularyLanguage={
              saveVocabularyLanguage
            }

            openMatching={
              openVocabularyMatching
            }

            goBack={() =>
              setPage(
                "vocabulary"
              )
            }

            styles={styles}
          />
        )}


        {/* ===================================================
            VOCABULARY FLASHCARDS
        =================================================== */}

        {page === "vocabularyFlashcards" &&
          selectedVocabularyTopic && (
            <VocabularyFlashcards
              terms={
                vocabularyTerms
              }

              getTranslationsForTerm={
                getTranslationsForTerm
              }

              submitVocabularyReview={
                submitVocabularyReview
              }

              submittingVocabularyReview={
                submittingVocabularyReview
              }

              vocabularyLanguage={
                vocabularyLanguage
              }

              goBack={() =>
                setPage(
                  "vocabularyTopic"
                )
              }

              styles={styles}
            />
          )}


        {/* ===================================================
            VOCABULARY MATCHING
        =================================================== */}

        {page === "vocabularyMatching" &&
          selectedVocabularyTopic && (
            <VocabularyMatching
              terms={
                vocabularyTerms
              }

              difficultyStats={
                vocabularyDifficultyStats
              }

              getTranslationsForTerm={
                getTranslationsForTerm
              }

              vocabularyLanguage={
                vocabularyLanguage
              }

              submitVocabularyMatchingResult={
                submitVocabularyMatchingResult
              }

              getVocabularyProgressForTerm={
                getVocabularyProgressForTerm
              }

              goBack={() =>
                setPage(
                  "vocabularyTopic"
                )
              }

              styles={styles}
            />
          )}


        {/* ===================================================
            VOCABULARY QUIZ
        =================================================== */}

        {page === "vocabularyQuiz" &&
          selectedVocabularyTopic && (
            <VocabularyQuiz
              terms={
                vocabularyTerms
              }

              difficultyStats={
                vocabularyDifficultyStats
              }

              getTranslationsForTerm={
                getTranslationsForTerm
              }

              getVocabularyProgressForTerm={
                getVocabularyProgressForTerm
              }

              vocabularyLanguage={
                vocabularyLanguage
              }

              submitVocabularyQuizResult={
                submitVocabularyQuizResult
              }

              goBack={() =>
                setPage(
                  "vocabularyTopic"
                )
              }

              styles={styles}
            />
          )}

        {/* ===================================================
            WRONG QUESTION V2
        =================================================== */}

        {page === "wrongQuestionHome" && (
          <WrongQuestionHomePage
            summary={
              wrongQuestionSummary
            }

            loading={
              loadingWrongQuestionSummary
            }

            startDueReview={
              startDueWrongQuestionReview
            }

            startActivePractice={
              startActiveWrongQuestionPractice
            }

            goBack={
              goBackToHome
            }

            styles={
              styles
            }
          />
        )}


        {/* ===================================================
            PRACTICE HOME
        =================================================== */}

        {page === "practiceHome" && (
          <PracticeHomePage
            startRandomPractice={
              startRandomPractice
            }

            startNewSmartPracticeRound={
              startNewSmartPracticeRound
            }

            openSubjectPractice={
              openSubjectPractice
            }

            goBack={
              goBackToHome
            }

            practiceMode={
              practiceMode
            }

            activePracticeSummary={
              activePracticeSummary
            }

            styles={styles}
          />
        )}


        {/* ===================================================
            PRACTICE SUBJECTS
        =================================================== */}

        {page === "practiceSubjects" && (
          <PracticeSubjectsPage
            domains={
              practiceDomains
            }

            loading={
              loadingPracticeSubjects
            }

            openDomain={
              openPracticeDomain
            }

            goBack={() =>
              setPage(
                "practiceHome"
              )
            }

            styles={styles}
          />
        )}


        {/* ===================================================
            PRACTICE TOPICS
        =================================================== */}

        {page === "practiceTopics" &&
          selectedPracticeDomain && (
            <PracticeTopicsPage
              domain={
                selectedPracticeDomain
              }

              topics={
                practiceTopics
              }

              activeSubjectPracticeSummaries={
                activeSubjectPracticeSummaries
              }

              loading={
                loadingPracticeSubjects
              }

              openTopic={
                openPracticeTopic
              }

              startNewSubjectPracticeRound={
                startNewSubjectPracticeRound
              }

              goBack={
                goBackToPracticeSubjects
              }

              styles={styles}
            />
          )}


        {/* ===================================================
            PRACTICE QUIZ
        =================================================== */}

        {page === "practiceQuiz" && (
          <PracticeQuestionPage
            currentIndex={
              currentIndex
            }

            questions={
              practiceQuestions
            }

            currentQuestion={
              currentPracticeQuestion
            }

            practiceTopic={
              selectedPracticeTopic
            }

            practiceSessionId={
              currentPracticeSessionId
            }

            restoredAnswers={
              restoredPracticeAnswers
            }

            submitPracticeAnswer={
              submitPracticeAnswer
            }

            submittingPracticeAnswer={
              submittingPracticeAnswer
            }

            goPrevious={
              goPreviousPractice
            }

            goNext={
              goNextPractice
            }

            finishRound={
              finishPracticeRound
            }

            goBack={
              practiceMode === "wrong_review" ||
              practiceMode === "wrong_active"
                ? openWrongQuestionCenter
                : practiceMode === "subject"
                ? () => setPage("practiceTopics")
                : openPractice
            }

            loading={
              loadingPracticeQuestions
            }

            styles={styles}
          />
        )}


        {/* ===================================================
            PRACTICE ROUND COMPLETE
        =================================================== */}

        {page ===
          "practiceRoundComplete" && (
          <PracticeRoundCompletePage
            sessionResult={
              practiceRoundResult
            }

            practiceMode={
              practiceMode
            }

            practiceTopic={
              selectedPracticeTopic
            }

            continuePractice={
              continuePracticeRound
            }

            finishPractice={
              finishPracticeFlow
            }

            loading={false}

            styles={styles}
          />
        )}
      </div>
    </div>
  );
}